import { Link, NavLink } from "react-router-dom";
import { Box, Button, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { DATE_FORMAT_FRONT } from "../../../utils/constants";

const account_name = {
  headerName: "Account",
  wrapText: true,
  autoHeight: true,
  sortable: true,
  field: "account_name",
  minWidth: 300,
  maxWidth: 300,
  filter: true,
  comparator: () => {}, //disable default sorting
  cellRenderer: (params) => (
    <Typography fontSize={12}>{params.data.account_name}</Typography>
  ),
};

const account_internal_id = {
  headerName: "Internal ID",
  wrapText: true,
  autoHeight: true,
  sortable: true,
  field: "internal_id",
  maxWidth: 120,
  filter: true,
  comparator: () => {}, //disable default sorting
  cellRenderer: (params) => (
    <Typography fontSize={12}>{params.data.internal_id}</Typography>
  ),
};

// Server-side sorted balance column. Net columns have credits applied;
// "_open" columns only count invoices that still owe money.
const balanceCol = (headerName, field, width = 100) => ({
  headerName,
  field,
  colId: field,
  sortable: true,
  minWidth: width,
  maxWidth: width,
  wrapText: true,
  autoHeight: true,
  valueGetter: (params) => params.data?.[field] || 0,
  comparator: () => {}, //disable default sorting
  cellRenderer: (params) => (
    <Typography fontSize={12}>{params.data?.[field]}</Typography>
  ),
});

// Aging buckets. Each one shows its net balance (credits applied) next to
// its open balance (only invoices that still owe money).
export const BALANCE_BUCKETS = [
  { field: "life_time_balance", label: "Lifetime" },
  { field: "balance_0", label: "Current" },
  { field: "balance_30", label: "30" },
  { field: "balance_60", label: "60" },
  { field: "balance_90", label: "90" },
  { field: "balance_120", label: "120" },
  { field: "balance_150", label: "150+" },
];

const balanceGroups = BALANCE_BUCKETS.map(({ field, label }) => ({
  headerName: label,
  // Must differ from the column ids, or the grid renames the columns and
  // server-side sorting gets the wrong field.
  groupId: `${field}_group`,
  children: [
    balanceCol("Net", field, field === "life_time_balance" ? 105 : 95),
    balanceCol(
      "Open",
      `${field}_open`,
      field === "life_time_balance" ? 105 : 95
    ),
  ],
}));

const managers = {
  sortable: true,
  minWidth: 150,
  maxWidth: 150,
  headerName: "Collectors",
  field: "collector",
  filter: true,
  valueGetter: (params) =>
    params.data?.managers?.map((manager) => manager.name).join(","),
  cellRenderer: (params) => {
    const managers = params.data?.managers
      ?.map((manager) => manager.name)
      .join(",");
    return (
      <Box>
        {managers && <Typography fontSize={12}>{managers}</Typography>}
        {!managers && (
          <Typography fontSize={12} color={"error"}>
            No manager!
          </Typography>
        )}
      </Box>
    );
  },
  wrapText: true,
  autoHeight: true,
  comparator: () => {}, //disable default sorting
};

const hasCollector = {
  sortable: true,
  minWidth: 120,
  maxWidth: 120,
  headerName: "Has Collectors",
  field: "hasCollector",
  filter: true,
  valueGetter: (params) =>
    params.data?.managers?.length > 0 ? "true" : "false",
  wrapText: true,
  autoHeight: true,
  hide: true,
};

const account_general_note = {
  sortable: true,
  minWidth: 180,
  headerName: "account_general_note",
  field: "account_general_note",
  filter: true,
  valueGetter: (params) => params.data?.account_general_note,
  wrapText: true,
  autoHeight: true,
  hide: true,
};

const task_date = {
  sortable: true,
  minWidth: 120,
  maxWidth: 120,
  headerName: "Date",
  field: "date",
  filter: true,
  valueGetter: (params) =>
    params.data.date ? dayjs(params.data.date).format("YYYY-MM-DD") : "-",
  cellRenderer: (params) => (
    <Typography fontSize={12}>
      {params.data.date
        ? dayjs(params.data.date).format(DATE_FORMAT_FRONT)
        : "-"}
    </Typography>
  ),
  wrapText: true,
  autoHeight: true,
  sort: "asc",
  comparator: () => {}, //disable default sorting
};

const CollectionsCols = [
  task_date,
  account_internal_id,
  account_name,
  managers,
  hasCollector,
  //last_call,
  ...balanceGroups,
];

// Columns the user can show or hide besides the balance columns.
export const OTHER_COLUMNS = [
  { field: "date", label: "Date" },
  { field: "internal_id", label: "Internal ID" },
  { field: "account_name", label: "Account" },
  { field: "collector", label: "Collectors" },
];

export default CollectionsCols;
